import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Event,
  EventEmitter,
  Watch,
  Listen,
} from '@stencil/core';
import dayjs from 'dayjs';

export interface AhaDatepickerRange extends HTMLAhaDatepickerRangeElement {}

/**
 * A component which wraps two `<aha-datepicker>` elements.
 */
@Component({
  tag: 'aha-datepicker-range',
  styleUrl: 'aha-datepicker-range.css',
  shadow: false,
  scoped: true,
})
export class AhaDatepickerRange {
  @Element() host!: HTMLAhaDatepickerRangeElement;

  private hasCustomStartButton = false;
  private hasCustomEndButton = false;
  private group = crypto.randomUUID();

  /** The name of the form control that will be submitted via a regular `<form>` submit. */
  @Prop({ reflect: true }) name?: string;
  @Watch('name')
  private watchName() {
    // Ensure that the name always has a `[]` at the end, since we are submitting 2 values (start and end).
    // Ex. if a `name` of `foo` is set, this will change the name to `foo[]`.
    if (this.name) {
      if (!/\[\]$/.test(this.name)) {
        this.name = `${this.name}[]`;
      }
    }
  }

  @Prop({ reflect: true, attribute: 'start-name' }) startName?: string;

  @Prop({ reflect: true, attribute: 'end-name' }) endName?: string;

  /** A prefix label for the start date. */
  @Prop({ reflect: true, attribute: 'start-label' }) startLabel?: string;

  /** A prefix label for the end date. */
  @Prop({ reflect: true, attribute: 'end-label' }) endLabel?: string;

  /** A symbol or string to use as the range separator. */
  @Prop({ reflect: true, attribute: 'separator' }) separator?: string = '-';

  /** The starting date. Should be of format `YYYY-MM-DD`. */
  @Prop({ reflect: true }) start?: string;

  /** Whether or not the start datepicker is invalid. */
  @Prop({ reflect: true, attribute: 'invalid-start' }) invalidStart?: string;

  /** Whether or not the end datepicker is invalid. */
  @Prop({ reflect: true, attribute: 'invalid-end' }) invalidEnd?: string;

  /** The ending date. Should be of format `YYYY-MM-DD`. */
  @Prop({ reflect: true }) end?: string;

  /** The text that will be shown when the `start` datepicker is empty. */
  @Prop({ reflect: true, attribute: 'placeholder-start' }) placeholderStart?: string;

  /** The text that will be shown when the `end` datepicker is empty. */
  @Prop({ reflect: true, attribute: 'placeholder-end' }) placeholderEnd?: string;

  /** Whether or not the `value` of the component can be set, or if it can only be read. */
  @Prop({ reflect: true, attribute: 'readonly' }) readOnly?: boolean;

  /** Whether or not the component is disabled. */
  @Prop({ reflect: true }) disabled?: boolean;

  /** Whether the date inputs will be displayed as [ISO 8061 format](https://www.iso.org/iso-8601-date-and-time-format.html) (YYYY-MM-DD) or according to localized formatting rules (eg. MM/DD/YYYY or DD/MM/YYYY, depending on locale specified in `window.navigator`). */
  @Prop({ reflect: true }) iso?: boolean;

  /** Whether or not a button should be shown next to the datepicker input fields. */
  @Prop({ reflect: true }) button?: boolean;

  /** If both dates are selected, changing one date will change the other date by the exact same amount.
   *
   * Ex. you move your start date forward by 5 days, the end date will also move forward by 5 days. */
  @Prop({ reflect: true, attribute: 'sync-dates' }) syncDates?: boolean;

  /**
   * Triggers when either the `start` or `end` value is changed.
   */
  @Event({
    eventName: 'aha-datepicker-range:change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  changeEvent!: EventEmitter;

  @Listen('aha-datepicker:change')
  handleDateInputChange(e: CustomEvent) {
    if (e.target && e.currentTarget) {
      const currentTarget = e.currentTarget as HTMLElement;
      const datepickers = Array.from(currentTarget.querySelectorAll('aha-datepicker'));
      // @ts-ignore
      const valueSetter = datepickers.indexOf(e.target) === 0 ? 'start' : 'end';
      this[valueSetter] = e.detail.value;
      this.changeEvent.emit(this.host);
    }
  }

  // When `syncDates` is true, dates will be kept in sync as they change. We need to only keep the date that we _didn't_ change in sync though.
  private preventDiffProcessing?: boolean;

  private handleChange(e: Event) {
    const target = e.target as HTMLElement;
    const dateElem = target.closest('aha-datepicker') as HTMLAhaDatepickerElement;
    let otherDateElem: any;
    let originalValue = dateElem === this.startDateElem ? this.start : this.end;

    if (dateElem === this.startDateElem) {
      otherDateElem = this.endDateElem;
      this.start = dateElem.value;
    } else {
      otherDateElem = this.startDateElem;
      this.end = dateElem.value;
    }

    // If either the start or end date has no value, then do nothing else.
    if (!this.start || !this.end) {
      this.preventDiffProcessing = undefined;
      return;
    }

    const date = dayjs(dateElem.value);
    const otherDate = dayjs(otherDateElem.value);

    if (this.syncDates) {
      if (typeof this.preventDiffProcessing === 'undefined' || this.preventDiffProcessing) {
        this.preventDiffProcessing = false;
      } else {
        this.preventDiffProcessing = true;
        const diff = dayjs(dateElem.value).diff(originalValue, 'day');
        const operator = diff < 0 ? 'subtract' : 'add';
        const newDate = otherDate[operator](Math.abs(diff), 'days');
        otherDateElem.value = newDate.format('YYYY-MM-DD');
      }
    } else {
      // If we are not syncing dates, then we need to make sure that the start date is not before the end date.
      // If the end is before the start, then we need to set the end date to the start date, and vice versa.
      if (dateElem === this.startDateElem) {
        if (date.isAfter(otherDate)) {
          otherDateElem.value = date.format('YYYY-MM-DD');
          dateElem.value = otherDate.format('YYYY-MM-DD');
        }
      } else {
        if (date.isBefore(otherDate)) {
          otherDateElem.value = date.format('YYYY-MM-DD');
          dateElem.value = otherDate.format('YYYY-MM-DD');
        }
      }
    }
  }

  private startDateElem?: HTMLAhaDatepickerElement;
  private endDateElem?: HTMLAhaDatepickerElement;

  connectedCallback() {
    this.watchName();
  }

  componentDidLoad() {
    this.handleChange = this.handleChange.bind(this);
    [this.startDateElem, this.endDateElem].forEach(node => {
      node?.addEventListener('change', this.handleChange);
    });
  }

  componentWillLoad() {
    if (this.host.querySelector(':scope [slot="start-button"]')) {
      this.hasCustomStartButton = true;
    }

    if (this.host.querySelector(':scope [slot="end-button"]')) {
      this.hasCustomEndButton = true;
    }
  }

  render() {
    return (
      <Host>
        {this.startLabel && <span class="mr-1">{this.startLabel}</span>}
        <aha-datepicker
          ref={e => (this.startDateElem = e)}
          name={this.startName || this.name}
          placeholder={this.placeholderStart}
          readOnly={this.readOnly}
          disabled={this.disabled}
          iso={this.iso}
          value={this.start}
          button={this.button}
          group={this.group}
        >
          {/* Because aha-datepicker-range has no shadowDOM (due to the need to surface form input elements to the lightDOM), we have to check if the instance has a custom button and do a bit of a hack to ensure its slotted into the aha-datepicker correctly. */}
          {this.hasCustomStartButton && (
            <div class="aha-datepicker-range__button" slot="button">
              <slot name="start-button"></slot>
            </div>
          )}
        </aha-datepicker>
        {this.endLabel && <span class="ml-3">{this.endLabel}</span>}
        <span>{this.separator}</span>
        <aha-datepicker
          ref={e => (this.endDateElem = e)}
          name={this.endName || this.name}
          placeholder={this.placeholderEnd}
          readOnly={this.readOnly}
          disabled={this.disabled}
          iso={this.iso}
          value={this.end}
          button={this.button}
          group={this.group}
        >
          {/* Because aha-datepicker-range has no shadowDOM (due to the need to surface form input elements to the lightDOM), we have to check if the instance has a custom button and do a bit of a hack to ensure its slotted into the aha-datepicker correctly. */}
          {this.hasCustomEndButton && (
            <div class="aha-datepicker-range__button" slot="button">
              <slot name="end-button"></slot>
            </div>
          )}
        </aha-datepicker>
      </Host>
    );
  }
}
